import { useState, useEffect } from 'react';
import { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { NewsArticle, NewsCategory } from '../types';
import {
  listNews,
  deleteNews,
  getNewsStats,
  getNewsSources,
  updateNews,
} from '../services/newsService';
import { listActiveCategories } from '../services/categoriesService';
import {
  IconNews,
  IconRss,
  IconCalendar,
  IconTrash,
  IconExternalLink,
  IconChevronLeft,
  IconChevronRight,
  IconSortAscending,
  IconSortDescending,
  IconTag,
  IconLock,
} from '@tabler/icons-react';
import ArticleCategoryModal from '../components/ArticleCategoryModal';

export default function NewsArticlesPage() {
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [categories, setCategories] = useState<NewsCategory[]>([]);
  const [sources, setSources] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showUncategorized, setShowUncategorized] = useState(false);
  const [sourceFilter, setSourceFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({ total: 0, today: 0, thisWeek: 0 });
  const [pageSize] = useState(25);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [pageHistory, setPageHistory] = useState<(QueryDocumentSnapshot<DocumentData> | null)[]>(
    []
  );
  const [sortBy, setSortBy] = useState<'publishedAt' | 'sortScore'>('sortScore');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [editingArticle, setEditingArticle] = useState<NewsArticle | null>(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadArticles();
  }, [
    categoryFilter,
    showUncategorized,
    sourceFilter,
    dateFrom,
    dateTo,
    searchQuery,
    sortBy,
    sortDirection,
  ]);

  const loadData = async () => {
    try {
      const [cats, statsData, sourcesData] = await Promise.all([
        listActiveCategories(),
        getNewsStats(),
        getNewsSources(),
      ]);
      setCategories(cats);
      setStats(statsData);
      setSources(sourcesData);
      await loadArticles();
    } finally {
      setLoading(false);
    }
  };

  const loadArticles = async (cursor: QueryDocumentSnapshot<DocumentData> | null = null) => {
    try {
      const result = await listNews({
        pageSize,
        lastDoc: cursor,
        categoryFilter: categoryFilter || undefined,
        showUncategorized,
        searchQuery: searchQuery || undefined,
        sortBy,
        sortDirection,
        sourceFilter: sourceFilter || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      });
      setArticles(result.articles);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (error) {
      console.error('Error loading articles:', error);
    }
  };

  function handleNextPage() {
    setPageHistory(h => [...h, lastDoc]);
    loadArticles(lastDoc);
  }

  function handlePreviousPage() {
    const prev = pageHistory[pageHistory.length - 2] ?? null;
    setPageHistory(h => h.slice(0, -1));
    loadArticles(prev);
  }

  const currentPage = pageHistory.length + 1;

  const handleSort = (column: 'publishedAt' | 'sortScore') => {
    if (sortBy === column) {
      // Toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to desc
      setSortBy(column);
      setSortDirection('desc');
    }
    // Reset pagination
    setPageHistory([]);
    setLastDoc(null);
  };

  const handleDelete = async (article: NewsArticle) => {
    if (!confirm(`Are you sure you want to delete "${article.title}"?`)) {
      return;
    }

    try {
      await deleteNews(article.id);
      await loadArticles();
      // Refresh stats
      const statsData = await getNewsStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error deleting article:', error);
      alert('Failed to delete article');
    }
  };

  const handleEditCategories = (article: NewsArticle) => {
    setEditingArticle(article);
    setShowCategoryModal(true);
  };

  const handleSaveCategories = async (
    articleId: string,
    categoryIds: string[],
    categoriesLocked: boolean
  ) => {
    try {
      await updateNews(articleId, {
        categoryIds: categoryIds.length > 0 ? categoryIds : null,
        hasCategories: categoryIds.length > 0,
        categoriesLocked,
      });
      const currentPageCursor = pageHistory[pageHistory.length - 1] ?? null;
      await loadArticles(currentPageCursor);
      setShowCategoryModal(false);
      setEditingArticle(null);
    } catch (error) {
      console.error('Error updating categories:', error);
      throw error;
    }
  };

  const handleClearFilters = () => {
    setCategoryFilter('');
    setShowUncategorized(false);
    setSourceFilter('');
    setDateFrom('');
    setDateTo('');
    setSearchQuery('');
    setPageHistory([]);
    setLastDoc(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const SortIcon = ({ column }: { column: 'publishedAt' | 'sortScore' }) => {
    if (sortBy !== column) return null;
    return sortDirection === 'asc' ? (
      <IconSortAscending className="icon icon-sm ms-1" size={16} />
    ) : (
      <IconSortDescending className="icon icon-sm ms-1" size={16} />
    );
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  return (
    <div className="page-header">
      <div className="row g-2 align-items-center">
        <div className="col">
          <div className="page-pretitle">News</div>
          <h2 className="page-title">Articles</h2>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="row row-cards mt-3">
        <div className="col-sm-6 col-lg-4">
          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="subheader">Total Articles</div>
              </div>
              <div className="h1 mb-0">{stats.total}</div>
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-lg-4">
          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="subheader">Today</div>
              </div>
              <div className="h1 mb-0">{stats.today}</div>
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-lg-4">
          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="subheader">This Week</div>
              </div>
              <div className="h1 mb-0">{stats.thisWeek}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mt-3">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Category</label>
              <select
                className="form-select"
                value={categoryFilter}
                onChange={e => {
                  setCategoryFilter(e.target.value);
                  setShowUncategorized(false);
                  setPageHistory([]);
                  setLastDoc(null);
                }}
                disabled={showUncategorized}
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name || cat.id}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Source</label>
              <select
                className="form-select"
                value={sourceFilter}
                onChange={e => {
                  setSourceFilter(e.target.value);
                  setPageHistory([]);
                  setLastDoc(null);
                }}
              >
                <option value="">All Sources</option>
                {sources.map(source => (
                  <option key={source} value={source}>
                    {source}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Date From</label>
              <input
                type="date"
                className="form-control"
                value={dateFrom}
                onChange={e => {
                  setDateFrom(e.target.value);
                  setPageHistory([]);
                  setLastDoc(null);
                }}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Date To</label>
              <input
                type="date"
                className="form-control"
                value={dateTo}
                onChange={e => {
                  setDateTo(e.target.value);
                  setPageHistory([]);
                  setLastDoc(null);
                }}
              />
            </div>
          </div>
          <div className="row g-3 mt-2">
            <div className="col-md-9">
              <label className="form-label">Search</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value);
                  setPageHistory([]);
                  setLastDoc(null);
                }}
              />
            </div>
            <div className="col-md-3 d-flex align-items-end">
              <div className="form-check form-switch mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="showUncategorized"
                  checked={showUncategorized}
                  onChange={e => {
                    setShowUncategorized(e.target.checked);
                    if (e.target.checked) {
                      setCategoryFilter('');
                    }
                    setPageHistory([]);
                    setLastDoc(null);
                  }}
                />
                <label className="form-check-label" htmlFor="showUncategorized">
                  Uncategorized Only
                </label>
              </div>
            </div>
          </div>
          {(categoryFilter ||
            showUncategorized ||
            sourceFilter ||
            dateFrom ||
            dateTo ||
            searchQuery) && (
            <div className="mt-3">
              <button className="btn btn-link btn-sm" onClick={handleClearFilters}>
                Clear all filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Articles Table */}
      <div className="card mt-3">
        <div className="table-responsive">
          <table className="table table-vcenter card-table">
            <thead>
              <tr>
                <th style={{ width: '60px' }}>Image</th>
                <th>Title</th>
                <th>Source</th>
                <th>Categories</th>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('publishedAt')}
                >
                  <div className="d-flex align-items-center">
                    Published
                    <SortIcon column="publishedAt" />
                  </div>
                </th>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none', width: '100px' }}
                  onClick={() => handleSort('sortScore')}
                >
                  <div className="d-flex align-items-center">
                    Score
                    <SortIcon column="sortScore" />
                  </div>
                </th>
                <th className="w-1">Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-5">
                    <div className="empty">
                      <div className="empty-icon">
                        <IconNews size={48} stroke={1.5} />
                      </div>
                      <p className="empty-title">No articles found</p>
                      <p className="empty-subtitle text-muted">
                        Try adjusting your filters or wait for RSS feeds to be processed
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                articles.map(article => (
                  <tr key={article.id}>
                    <td>
                      {article.imageUrl ? (
                        <img
                          src={article.imageUrl}
                          alt={article.title}
                          className="avatar"
                          style={{ objectFit: 'cover' }}
                        />
                      ) : (
                        <span className="avatar" style={{ backgroundColor: '#f3f4f6' }}>
                          <IconNews size={24} />
                        </span>
                      )}
                    </td>
                    <td>
                      <div>
                        <div className="text-reset">{truncateText(article.title, 80)}</div>
                        {article.summary && (
                          <div className="text-muted small mt-1">
                            {truncateText(article.summary, 120)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-1">
                        <IconRss className="icon icon-sm" size={16} />
                        <span className="text-muted">{article.source}</span>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex gap-1 flex-wrap align-items-center">
                        {article.categoryIds && article.categoryIds.length > 0 ? (
                          article.categoryIds.map(categoryId => {
                            const category = categories.find(c => c.id === categoryId);
                            return (
                              <span key={categoryId} className="badge bg-primary-lt">
                                {category?.name || categoryId}
                              </span>
                            );
                          })
                        ) : (
                          <span className="badge bg-secondary-lt">Uncategorized</span>
                        )}
                        {article.categoriesLocked && (
                          <IconLock
                            size={14}
                            className="text-muted"
                            title="Categories locked (manually assigned)"
                          />
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-1 text-muted small">
                        <IconCalendar className="icon icon-sm" size={16} />
                        {formatDate(article.publishedAt)}
                      </div>
                    </td>
                    <td>
                      <span className="badge bg-blue-lt">
                        {article.sortScore?.toFixed(2) || '0.00'}
                      </span>
                    </td>
                    <td>
                      <div className="btn-list flex-nowrap">
                        <button
                          onClick={() => handleEditCategories(article)}
                          className="btn btn-icon"
                          title="Edit Categories"
                        >
                          <IconTag className="icon" />
                        </button>
                        {article.sourceUrl && (
                          <a
                            href={article.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-icon"
                            title="View Article"
                          >
                            <IconExternalLink className="icon" />
                          </a>
                        )}
                        <button
                          onClick={() => handleDelete(article)}
                          className="btn btn-icon text-danger"
                          title="Delete"
                        >
                          <IconTrash className="icon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="card-footer d-flex align-items-center">
          <p className="m-0 text-muted">Page {currentPage}</p>
          <ul className="pagination m-0 ms-auto">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
              >
                <IconChevronLeft size={16} />
                prev
              </button>
            </li>
            <li className={`page-item ${!hasMore ? 'disabled' : ''}`}>
              <button className="page-link" onClick={handleNextPage} disabled={!hasMore}>
                next
                <IconChevronRight size={16} />
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Category Edit Modal */}
      {showCategoryModal && editingArticle && (
        <ArticleCategoryModal
          article={editingArticle}
          categories={categories}
          onSave={handleSaveCategories}
          onClose={() => {
            setShowCategoryModal(false);
            setEditingArticle(null);
          }}
        />
      )}
    </div>
  );
}
